<template>
    <div class="p-12 container">
        <h2>Create a Dedicated Server</h2>

        <div class="flex flex-wrap">
            <div class="w-full pb-8 md:w-2/3 md:pr-4">

                <ul class="breadcrumb bg-grey-100 rounded p-4 mb-8">
                    <li class="breadcrumb-item"><router-link to="/order">Order</router-link></li>
                    <li class="breadcrumb-item"><router-link to="/order/dedicated-server">Dedicated Servers</router-link></li>
                    <li class="breadcrumb-item active">Options</li>
                </ul>

                <!-- Wait for configs to be loaded before outputting template  -->
                <template v-if="!ready">
                    <sau-loading>
                        <template slot="message">Fetching options</template>
                    </sau-loading>
                </template>

                <template v-else>
                    <div class="mb-4 flex">
                        <svg class="text-green block w-12 h-12 pt-1 fill-current" aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><g class="fa-group"><path class="fa-secondary" fill="currentColor" d="M248 160H40a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h208a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8zm-24 88v-16a8 8 0 0 0-8-8H8a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h208a8 8 0 0 0 8-8zm-48 104a80 80 0 1 0 80 80 80 80 0 0 0-80-80zm288 0a80 80 0 1 0 80 80 80 80 0 0 0-80-80zM280 96H8a8 8 0 0 0-8 8v16a8 8 0 0 0 8 8h272a8 8 0 0 0 8-8v-16a8 8 0 0 0-8-8z" opacity="0.4"></path><path class="fa-primary" fill="currentColor" d="M624 352h-16V243.9a48 48 0 0 0-14.1-33.9L494 110.1A48 48 0 0 0 460.1 96H416V48a48 48 0 0 0-48-48H112a48 48 0 0 0-48 48v48h216a8 8 0 0 1 8 8v16a8 8 0 0 1-8 8H64v32h184a8 8 0 0 1 8 8v16a8 8 0 0 1-8 8H64v32h152a8 8 0 0 1 8 8v16a8 8 0 0 1-8 8H64v112a47.74 47.74 0 0 0 7 25 112 112 0 0 1 215.86 23h66.28a112 112 0 0 1 221.72 0H624a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm-64-96H416V144h44.1l99.9 99.9z"></path></g></svg>
                        <div class="ml-4">
                            <p class="mb-0">Current delivery time on this server:</p>
                            <template>
                                <p v-if="provisioningTime" class="text-green text-xl leading-none">
                                    {{ provisioningTime }}
                                </p>
                                <p v-else class="text-blue text-xl leading-none">
                                    <i class="far fa-spinner-third fa-spin"></i>
                                </p>
                            </template>
                        </div>
                    </div>

                    <div class="mb-12 flex">
                        <i class="fal fa-server block text-6xl pt-1"></i>
                        <div class="ml-8">
                            <p class="mb-0">
                                <strong class="sau-font-bold">Location:</strong>
                                <template v-if="activeDediSpecs && activeDediSpecs.location.id">
                                    {{ activeDediSpecs.location.description }}
                                </template>
                                <template v-else>
                                    <i class="far fa-spinner-third fa-spin"></i>
                                </template>
                            </p>
                            <p class="mb-0">
                                <strong class="sau-font-bold">CPU:</strong>
                                <template v-if="activeDediSpecs && activeDediSpecs.cpu">{{ activeDediSpecs.cpu.description }}</template>
                            </p>
                            <p class="mb-0">
                                <strong class="sau-font-bold">RAM:</strong>
                                <template v-if="activeDediSpecs && activeDediSpecs.ram">{{ activeDediSpecs.ram.description }}</template>
                            </p>
                            <p class="mb-0">
                                <strong class="sau-font-bold">STORAGE:</strong>
                                <template v-if="activeDediSpecs && activeDediSpecs.storage && Array.isArray(activeDediSpecs.storage)">{{ activeDediSpecs.storage.map(item => item.description).join('; ') }}</template>
                            </p>
                        </div>
                    </div>

                    <div class="row mb-5">
                        <div class="col-12">
                            <h4>Select a client operating system image</h4>
                        </div>

                        <div class="col-md-6 text-center os mb-5" v-for="(os, index) in supportedOsProviders" :key="index" @click="selectOsProvider(index)">
                            <div class="col-12 mb-0 sau-bx border-bottom-0 py-6 os" :class="index == osProvider ? 'selected' : ''">
                                <img :src="osProviderImage(index)" :alt="index" class="grayscale">
                                <p>{{ index }}</p>
                            </div>
                            <div class="col-12 sau-bx p-0" :class="index == osProvider ? 'selected' : ''">
                                <select class="form-control sau-select" @change="updateOs" ref="osRef">
                                    <option v-for="(distribution, key) in os" :key="key" :value="distribution.id" :selected="distribution.id == dedi.os.id">{{ distribution.version }}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-5" v-if="windowsIsSelected && selectedOs">
                        <div class="col-12">
                            <h4>Windows Server Licence</h4>
                            <div class="col-12 sau-bx p-0">
                                <select class="form-control sau-select" @change="handleWindowsAddonLicencing">
                                    <option v-for="(licence) in windowsLicences" :key="licence.id" :value="licence.id" :selected="licence.id === windowsServerLicence">
                                        {{ licence.required_amount }}x {{ licence.name }} - {{ licence.total_cost | currency }} 
                                    </option>    
                                </select>
                            </div>
                        </div>
                    </div>
                    
                    
                    <div v-if="windowsIsSelected && selectedOs && activeWindowsLicence" class="my-6 p-6 bg-primary-100 w-full text-blue-800 shadow-lg border border-blue-300 flex md:flex-row justify-between items-center flex-col gap-4 md:gap-0">
                        <div>
                            <p class="mb-0 text-blue-600">A {{ activeWindowsLicence.name }} covers the use of 2 physical CPU cores, and is charged at {{ activeWindowsLicence.price[userCurrency] | currency }} per licence. The minimum is 8 cores.</p>
                            <p class="mb-0 text-blue-600"><small>The selected server has {{ activeDediSpecs.cpu.cores }} CPU cores, making the total cost of licencing {{ activeWindowsLicence.total_cost | currency }}.</small></p>
                        </div>
                    </div>

                    <div class="row mb-12" v-if="dedi.custom">
                        <div class="col-12">
                            <h4>Select a location</h4>
                        </div>
                        <div class="col-12 text-center">
                            <div class="sau-bx border-bottom-0 p-6">
                                <h4 class="mb-0">Location</h4>
                            </div>
                        </div>
                        <div class="col-12">
                            <div class="sau-bx bx-pad text-center">
                                <div class="sau-bx">
                                    <select v-model="location" class="form-control sau-select" @change="updateDedi({ location: location })">
                                        <option v-for="(location, index) in config.location" :key="index" :value="location.id">{{ location.name }} - {{ location.price[userCurrency] | currency }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-12">
                        <div class="col-12">
                            <h4>Hostname</h4>
                            <p class="mb-2">If no hostname is provided, one will automatically be assigned for you.</p>
                            <input v-model="hostname" type="text" @blur="updateHostname({ hostname: hostname })" class="form-control sau-bx w-100" placeholder="myserver.example.com">
                            <p v-if="! hostnameValid" class="mb-0"><small class="text-small text-red">Sorry, that hostname is not valid.</small></p>
                        </div>
                    </div>

                    <div
                        v-if="dedi.custom"
                        class="row mb-12"
                        :class="{
                            'active': dedi.custom,
                        }">
                        <div class="col-12">
                            <h4>Configuration</h4>
                        </div>
                        <div class="col-md-4 mb-0 text-center">
                            <div class="sau-bx p-6 border-bottom-0">
                                <h5 class="mb-0">CPU</h5>
                            </div>
                            <div class="sau-bx">
                                <select v-model="dedi.cpu.id" class="form-control sau-select" @change="updateDedi({ cpu: dedi.cpu.id })">
                                    <option v-for="(cpu, index) in filteredConfigOptions.cpu" :key="index" :value="cpu.id">{{ cpu.description }} - {{ cpu.monthly_price | currency }}</option>
                                </select>
                            </div>
                        </div>

                        <div class="col-md-4 mb-0 text-center">
                            <div class="sau-bx p-6 border-bottom-0">
                                <h5 class="mb-0">RAM</h5>
                            </div>
                            <div class="sau-bx">
                                <select v-model="dedi.ram.id" class="form-control sau-select" @change="updateDedi({ ram: dedi.ram.id })">
                                    <option v-for="(ram, index) in filteredConfigOptions.ram" :key="index" :value="ram.id">{{ ram.description }} - {{ram.monthly_price | currency}}</option>
                                </select>
                            </div>
                        </div>

                        <div class="col-md-4 mb-0 text-center">
                            <div class="sau-bx p-6 border-bottom-0">
                                <h5 class="mb-0">Bandwidth</h5>
                            </div>
                            <div class="sau-bx">
                                <select v-model="bandwidth" class="form-control sau-select" @change="updateDedi({ bandwidth: bandwidth })">
                                    <option v-for="(bandwidth, index) in sortedBandwidth" :key="index" :value="bandwidth.id">{{ bandwidth.description }} - {{ bandwidth.price[userCurrency] | currency }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div
                        v-else-if="dedi.rtg"
                        class="row mb-12 active"
                    >
                        <div class="col-12">
                            <h4>Configuration</h4>
                        </div>
                        <div class="col-md-4 mb-0 text-center">
                            <div class="sau-bx p-6 border-bottom-0">
                                <h5 class="mb-0">Bandwidth</h5>
                            </div>
                            <div class="sau-bx">
                                <select v-model="bandwidth" class="form-control sau-select" @change="updateDedi({ bandwidth: bandwidth })">
                                    <option v-for="(bandwidth, index) in config.bandwidth" :key="index" :value="bandwidth.id">{{ bandwidth.description }} - {{ bandwidth.price[userCurrency] | currency }}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-12 transition-bg" v-if="dedi.custom" :class="{ 'active':dedi.custom}">
                        <div class="col-12">
                            <h4>Storage</h4>
                        </div>
                        <div class="col-12">
                            <storage-set ref="storage" :type="type" :data="data" :dedi="dedi"></storage-set>
                        </div>
                    </div>


                    <div class="row mb-12" v-if="activeOsSupportsLicences">
                        <div class="col-12">
                            <h4>Control Panel Licences</h4>
                        </div>
                        <div class="col-12">
                            <div class="sau-bx">
                                <select class="form-control sau-select" v-model="licence" @change="updateLicences({ licence: licence })">
                                    <option selected value>None</option>
                                    <option v-for="(option, index) in config.addons.licences" :key="index" :value="option.id">
                                        <template v-if="option.price[userCurrency] > 0">{{ option.name }} - {{ option.price[userCurrency] | currency }}</template>
                                        <template v-else>{{ option.name }}</template>
                                    </option>
                                </select>
                            </div>
                            <small v-if="licenceDescription" class="text-xs text-grey-700" v-html="licenceDescription"></small>
                        </div>
                    </div>


                    <div class="row mb-12">
                        <div class="col-12">
                            <h4>Additional Options</h4>
                        </div>
                        <div class="col-12">
                            <div class="sau-bx p-6">

                                <label for="backupStorage">Backup Storage</label>
                                <div class="sau-bx mb-6">
                                    <select id="backupStorage" class="form-control sau-select" v-model="backupStorage" @change="updateAddons({ backup_storage: backupStorage })">
                                        <option selected value>None</option>
                                        <option v-for="(option, index) in config.addons.backups.hard_quota" :key="index" :value="option.id">{{ option.name }} - {{ option.price[userCurrency] | currency }}</option>
                                    </select>
                                </div>

                                <label for="backupFrequency">Backup Frequency</label>
                                <div class="sau-bx mb-6">
                                    <select id="backupFrequency" class="form-control sau-select" v-model="backupFrequency" @change="updateAddons({ backup_frequency: backupFrequency })" :disabled="backupStorage === ''">
                                        <option v-if="! this.backupFrequency" selected value>None</option>
                                        <option v-else v-for="(option, index) in config.addons.backups.replication_frequency" :key="index" :value="option.id">{{ option.name }} - {{ option.price[userCurrency] | currency }}</option>
                                    </select>
                                </div>

                                <label for="additionalIps">Additional IP's</label>
                                <div class="sau-bx mb-6">
                                    <select id="additionalIps" class="form-control sau-select" v-model="additionalIps" @change="updateAddons({ additional_ips: additionalIps })">
                                        <option selected value>None</option>
                                        <option v-for="(option, index) in sortedIps" :key="index" :value="option.id">{{ option.name }} - {{ option.price[userCurrency] | currency }}</option>
                                    </select>
                                </div>

                                <!-- <label for="management" id="management">Management</label>
                                <div class="sau-bx mb-6">
                                    <select id="management" class="form-control sau-select" v-model="management" @change="updateAddons({ management: management })">
                                        <option v-for="(option, index) in config.addons.management" :key="index" :value="option.id">{{ option.name }} - {{ option.price[userCurrency] | currency }}</option>
                                    </select>
                                </div> -->
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <sau-ordering-sidebar :data="data" :order="order" :processing="! ready"></sau-ordering-sidebar>
        </div>
    </div>
</template>

<script>
import StorageSet from "./StorageSet";

export default {
    props: {
        data: Object,
        order: Object,
        featuredServerHashes: Array
    },

    components: {
        StorageSet
    },

    data() {
        return {
            config: {},
            osProvider: "",
            osVersion: "",
            type: "dedicated-server",
            licence: "",
            location: "",
            hostname: "",
            cpu: "",
            ram: "",
            bandwidth: "",
            propertiesRequiringId: ["cpu", "ram", "bandwidth", "storage", "os", "location"],
            visibleStorageSets: 2,
            additionalIps: null,
            // management: null,
            backupFrequency: null,
            backupStorage: null,
            dedi: {},
            ready: false,
            provisioningTimesHumanReadable: {},
            osProvidersThatSupportLicences: ['Control Panels'],
            hostnameRegex: /^([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])(\.([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]))*$/,
            enterpriseCpus: [273, 266, 279, 264, 285, 49, 135, 370],
            windowsServerLicence: null
        };
    },

    mounted() {
        let locations = this.data.config.products.dedicatedserver.location;
        Object.keys(locations).forEach(locationName => {
            let provisioningTimeInSeconds = locations[locationName].provisioning_time_in_seconds
                ? locations[locationName].provisioning_time_in_seconds
                : (7 * 60 * 60 * 24); // days * seconds in a minute * minutes in an hour * hours in a day

            this.provisioningTimesHumanReadable[locationName] = moment()
                .add(provisioningTimeInSeconds, 'seconds')
                .fromNow()
                .replace('days', 'business days')
                .replace('in ', '');
        });

        this.createOrder().then(response => {
            this.getOrderSummary(response.data.slug).then(response => {
                this.setUp();
            });
        });
        window.bus.$on("order.items.addServerToOrderViaHash", response => {
            this.ready = false;
        });
        window.bus.$on("order.refresh", () => (this.ready = true));
    },

    methods: {
        setUp() {
            // Active dedi is set when a user reaches this page via the preconfigured dedis
            if (this.data.active.dedicatedServer) {
                this.dedi = this.order.items.find(item => item.type === this.type && item.slug === this.data.active.dedicatedServer);
            } else {
                if (this.$route.path === "/order/dedicated-server/custom") {
                    // If users directly navigate to this page with no active dedi, assume the most recently added one is active
                    let dedis = this.order.items.filter(item => {
                        return item.type === this.type;
                    });
                    // If there are multiple dedis in the order just mark the first one as active (editing)
                    this.dedi = Array.isArray(dedis) ? dedis[0] : dedi;

                    // This triggers the watcher which triggers the same function again so return the result so it stops executing
                    return (this.data.active.dedicatedServer = this.dedi.slug);
                }

                return;
            }

            // If this page is hit with no dedis in the order, send them back to preconfig page to begin the order
            if (!this.dedi) this.$router.push("/order/dedicated-server");

            this.config = this.data.config.products.dedicatedserver;

            // Bind the select models and set a default value of empty string so that the first option in the selects with a value="" will be prepopulated. Default null for unset data can't be used to prefill in vue
            // This should to be revisted
            this.additionalIps = this.dedi.addons && this.dedi.addons.additional_ips ? this.dedi.addons.additional_ips.id : "";
            this.backupFrequency = this.dedi.addons && this.dedi.addons.backup_frequency ? this.dedi.addons.backup_frequency.id : "";
            this.backupStorage = this.dedi.addons && this.dedi.addons.backup_storage ? this.dedi.addons.backup_storage.id : "";

            // this.management = this.dedi.addons && this.dedi.addons.management ? this.dedi.addons.management.id : "";

            (this.licence = this.dedi.licence && this.dedi.licence.id ? this.dedi.licence.id : ""), (this.location = this.dedi.location ? this.dedi.location.id : null);

            /**
             * Pre select the first OS if nothing is already set
             */
            if (this.dedi.os.id) {
                this.osVersion = this.dedi.os.id;
                this.osProvider = this.dedi.os.provider;
            } else {
                this.osProvider = "Linux";
                this.osVersion = this.data.config.products.dedicatedserver.os.Linux[0].id;
            }

            /**
             * Pre select bandwidth
             */
            if (this.dedi.bandwidth.id) {
                this.bandwidth = this.dedi.bandwidth.id;
            } else {
                // this.bandwidth = this.data.config.products.dedicatedserver.bandwidth[0].id;
                this.bandwidth = this.sortedBandwidth[0].id;
            }

            if (!this.location) {
                this.location = this.config.location.Sydney.id;
            }
            // if (this.management == "") {
            //     // pluck the ID for self management from the config array and save - move to backend later :D
            //     this.management = this.config.addons.management.find(mgmt => mgmt.name.includes("Self")).id;

            //     this.updateAddons({ management: this.management });
            // }

            if (!this.dedi.bandwidth.id || !this.dedi.os.id || !this.location) {
                this.updateDedi({ bandwidth: this.bandwidth, os: this.osVersion, location: this.location });
            }

            // Count the storage sets from existing order or default to 2 visible
            // this.visibleStorageSets = this.dedi.storage.length || 2;

            // Check dedi to see if the property 'id' exists on each of the required fields, if null add an empty ID key. The ID is required for model binding in the template.
            Object.keys(this.dedi).forEach(property => {
                if (this.propertiesRequiringId.indexOf(property) != -1 && this.dedi[property] == null) {
                    this.dedi[property] = { id: "" };
                }
            });

            this.windowsServerLicence = _.get(this.dedi, 'addons.windows_server_licence.id', null);

            this.ready = true;

            if (this.$refs.storage) {
                /** Update storage set copmponent */
                this.$refs.storage.setUp();
            }
        },

        selectOsProvider(os) {
            this.osProvider = os;
            let index = Object.keys(this.config.os).indexOf(os);
            this.updateDedi({
                os: this.$refs.osRef[index].value
            });
            this.applyRequiredLicence(this.$refs.osRef[index].value);
        },

        updateDedi(data) {
            window.bus.$emit("order.items.update", this.type, this.dedi.slug, data);
            // window.bus.$on("order.items.updated", response => {
            //     if (response.data.type === "dedicated-server" && response.data.slug === this.slug) {
            //         this.dedi = response.data;
            //     }
            // });
        },

        updateHostname(data) {
            // Ensure the hostname is valid before updating
            if(this.hostnameValid) {
                this.updateDedi(data);
            }
        },

        updateAddons(data) {
            this.updateDediAddons(this.order.slug, this.type, this.dedi.slug, data).then(response => {
                window.bus.$emit("order.refresh");
            });
        },

        updateLicences(data) {
            this.updateDediLicences(this.order.slug, this.type, this.dedi.slug, data).then(response => {
                window.bus.$emit("order.refresh");
            });
        },

        osProviderImage(os) {
            const lower = os.toLowerCase();
            switch(lower) {
                case 'linux':
                case 'windows':
                case 'vmware':
                case 'control panels':
                    return `/img/${_.kebabCase(os)}.gif`;
                default:
                    return '/img/laptop-code.png';
            }
        },

        removeSet(index) {
            this.visibleStorageSets--;
            this.dedi.storage = this.dedi.storage.filter((value, key) => key != index);

            // Loop through the storage set for this dedi and filter out the required properties for the endpoint, convert to json and send
            this.updateDedi({
                storage: JSON.stringify(
                    this.dedi.storage.map(set => {
                        return _.pick(set, ["id", "raidType", "set"]);
                    })
                )
            });
        },

        applyRequiredLicence(osId) {
            let requiredLicence = this.config.addons.licences.find(licence => licence.os_id == osId);

            if (requiredLicence && requiredLicence.id != this.licence) {
                this.licence = requiredLicence.id;
                this.updateLicences({ licence: requiredLicence.id });
            }
        },

        updateOs(e) {
            // This is so we can have multiple selects changing the same value - using v-model would cause the values of the other selects to be removed
            this.updateDedi({
                os: e.target.value
            });
            this.applyRequiredLicence(e.target.value);
        },

        customise() {
            this.dedi.custom = 1;
            this.updateDedi({ custom: 1, rtg: 0 });
        },

        newServer() {
            setTimeout(() => {
                this.$router.push(`/order/dedicated-server`);
            }, 1000);
            this.$parent.removeItem({ slug: this.dedi.slug, type: "dedicated-server" });
        },

        handleWindowsAddonLicencing(e = null) {
            const currentLicence = this.windowsServerLicence;

            if(_.get(e, 'target.value', false)) {

                this.windowsServerLicence = e.target.value;
            
            } else if(this.windowsIsSelected) {
                if(! this.windowsServerLicence) {
                    this.windowsServerLicence = this.windowsLicences[0].id;
                }
            } else {
                this.windowsServerLicence = null;
            }
            if(this.windowsServerLicence != currentLicence) {
                this.updateAddons({ windows_server_licence: this.windowsServerLicence });
            }
        }
    },

    computed: {
        activeDedi() {
            return this.data.active.dedicatedServer;
        },
        sortedBandwidth() {
            return _.sortBy(this.config.bandwidth, ["order"]);
        },
        activeDediSpecs() {
            return this.order.items.find(item => item.type === "dedicated-server" && item.slug === this.data.active.dedicatedServer);
        },
        provisioningTime() {

            if (!this.activeDediSpecs)
                return null;

            if(this.activeDediSpecs.inStock) {
                return '7 minutes!';
            }

            if (_.get(this, 'activeDediSpecs.os.provider', '').toLowerCase() == 'vmware') {
                return this.activeDediSpecs.raid_card ? '7 minutes!' : this.provisioningTimesHumanReadable[this.activeDediSpecs.location.description];
            }

            if (this.activeDediSpecs.rtg) {
                return '7 minutes!';
            }

            // Enterprise servers have a 30 day lead time
            if(this.activeDediSpecs) {
                if(this.enterpriseCpus.includes(this.activeDediSpecs.cpu.id)) {
                    return '30 business days';
                }
            }

            if (this.activeDediSpecs) {
                let cpuWhmcsId = this.data.config.products.dedicatedserver.cpu.find(cpu => cpu.id == this.activeDediSpecs.cpu.id).pid;
                if (this.provisioningTimesHumanReadable['cpu:' + cpuWhmcsId])
                    return this.provisioningTimesHumanReadable['cpu:' + cpuWhmcsId];
            }

            if (this.activeDediSpecs.location.id) {
                return  this.provisioningTimesHumanReadable[this.activeDediSpecs.location.description];
            }

            return null;
        },

        licenceDescription() {
            const licence = this.config.addons.licences.find(
                licence => licence.id == this.licence
            );

            return licence ? licence.description : null;
        },

        sortedIps() {
            return this.config.addons.additional_ips.sort((a, b) => a.price[this.userCurrency] - b.price[this.userCurrency]);
        },

        activeOsSupportsLicences() {
            return this.osProvidersThatSupportLicences.includes(this.osProvider);
        },

        filteredConfigOptions() {
            // We need to hide specific hardware which is -not- grandfathered, but should not be a selectable option
            // So just filter out any CPU/RAM greater than $10,000 per month
            if(! Object.keys(this.config).length) return {};

            const maxMonthlyPrice = 10000;
            const config = this.data.config.products.dedicatedserver;

            return {
                ...this.config,
                ...{
                    cpu: this.config.cpu.filter((cpu) => {
                        return parseFloat(cpu.monthly_price) < maxMonthlyPrice;
                    }),
                    ram: this.config.ram.filter((ram) => {
                        return parseFloat(ram.monthly_price) < maxMonthlyPrice;
                    }),
                }
            }
        },

        hostnameValid() {
            if(this.hostname.length === 0 ) return true;
            return this.hostnameRegex.test(this.hostname) && this.hostname.length < 255;
        },

        windowsIsSelected() {
            return _.get(this.activeDediSpecs, 'os.provider', '').toLowerCase() === 'windows';
        },

        selectedOs() {
            return _.get(this.config, 'os.' + this.activeDediSpecs.os.provider, []).find((distro) => distro.id == this.activeDediSpecs.os.id);
        },

        windowsLicences() {
            return _.get(this.filteredConfigOptions, 'addons.windows_server_licence', [])
                .map((licence) => {
                    const billableCores = this.activeDediSpecs.cpu.cores / 2 > 4 ? this.activeDediSpecs.cpu.cores / 2 : 4;
                    return {...licence, ...{ required_amount: billableCores, total_cost: billableCores * licence.price[this.userCurrency] }};
                })
                .sort((a, b) => a.price[this.userCurrency] - b.price[this.userCurrency]);
        },

        activeWindowsLicence() {
            return this.windowsLicences.find((licence) => licence.id == this.windowsServerLicence);
        },

        supportedOsProviders() {
            // A list of OS providers that are supported by the current dedicated server
            // Servers without hardware raid do not support VMware(Esxi)
            if(this.dedi.raid_card) return this.config.os;

            return Object.fromEntries(
                Object.entries(this.config.os)
                    .filter(([key]) => !['VMware'].includes(key))
            );
        },
    },

    watch: {
        activeDedi() {
            this.setUp();
        },

        backupStorage: function(val) {
            if (val == "") {
                this.backupFrequency = "";
                this.updateAddons({ backup_frequency: "" });
            } else {
                if (!this.backupFrequency) {
                    this.backupFrequency = this.config.addons.backups.replication_frequency[0].id;
                    this.updateAddons({ backup_frequency: this.backupFrequency });
                }
            }
        },

        activeOsSupportsLicences: function() {
            if(! this.activeOsSupportsLicences) {
                this.licence = '';
                this.updateLicences({ licence: this.licence })
            }
        },

        windowsIsSelected: function() {
            this.handleWindowsAddonLicencing();
        }
    }
};
</script>

<style lang="scss" scoped>
.sau-select {
    border: none;
    text-align-last: center; // Chrome
    text-align: center; // Firefox
    padding-top: 0px !important;
    &:focus,
    &:hover {
        outline: none;
        box-shadow: none;
        border: none;
    }
}
.os {
    &.selected,
    &:hover {
        .grayscale {
            filter: grayscale(0);
        }
    }

    .selected {
        * {
            color: #0769ba;
        }
    }

    &:hover {
        cursor: pointer;

        .grayscale {
            filter: grayscale(0);
        }
    }
}
.grayscale {
    filter: grayscale(100);

    &:hover {
        filter: grayscale(0);
    }
}
.selected {
    box-shadow: 0 0 2px #0769ba;
    color: #0769ba;
}
.mw-50 {
    max-width: 50%;
}
.mw-75 {
    max-width: 75%;
}

select {
    -moz-appearance: auto !important;
    -webkit-appearance: auto !important;
}
</style>
